<template>
  <div
    class="app-avatar"
    :class="{
      'app-avatar--xs': size === 'xs',
      'app-avatar--s': size === 's',
      'app-avatar--m': size === 'm',
    }"
    :style="avatarStyle"
  >
    <div v-if="hasInitials" class="app-avatar-text">
      {{ initials }}
    </div>

    <app-material-icon
      v-else
      :name="defaultIcon"
      :type="ICON_TYPE.FA"
      :weight="defaultIconWeight"
      :size="iconSize"
      :color="iconColor"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue'

import AppMaterialIcon from '@/design-system/icons/AppMaterialIcon.vue'

type AppAvatarSize = 'xs' | 's' | 'm'
type AppIconWeight = 'fak' | 'far' | 'fas'

const ICON_TYPE = {
  FA: 'fa',
  MATERIAL: 'material',
} as const

export default defineComponent({
  name: 'AppAvatar',
  components: {
    AppMaterialIcon,
  },
  props: {
    /**
     * The text to display in avatar
     */
    initials: {
      type: String,
      default: '',
    },
    /**
     * Default icon when initials are empty
     */
    defaultIcon: {
      type: String,
      default: 'user',
    },
    /**
     * Default icon weight when initials are empty
     */
    defaultIconWeight: {
      type: String as PropType<AppIconWeight>,
      default: 'far',
    },
    /**
     * Size of the avatar
     * @values: xs, s, m
     */
    size: {
      type: String as PropType<AppAvatarSize>,
      default: 's',
    },
    color: {
      type: String,
      default: null,
    },
    backgroundColor: {
      type: String,
      default: null,
    },
    borderColor: {
      type: String,
      default: null,
    },
  },
  setup (props) {
    const hasInitials = computed(() => !!props.initials?.trim())

    const avatarStyle = computed(() => ({
      color: props.color || undefined,
      backgroundColor: props.backgroundColor || undefined,
      border: props.borderColor ? `1px solid ${props.borderColor}` : undefined,
    }))

    const iconSize = computed(() => {
      if (props.size === 'xs') return 'xs'
      if (props.size === 'm') return 'md'
      return 'sm'
    })

    const iconColor = computed(() => props.color || 'currentColor')

    return {
      hasInitials,
      avatarStyle,
      iconSize,
      iconColor,
      ICON_TYPE,
    }
  },
})
</script>

<style scoped lang="scss">
.app-avatar {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: rgb(var(--v-theme-grey-700));
  background-color: rgb(var(--v-theme-grey-300));
  font-weight: 500;

  &--xs {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  &--s {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }

  &--m {
    width: 48px;
    height: 48px;
    font-size: 24px;
  }

  &-text {
    text-transform: uppercase;
  }
}
</style>
